import type { TableAction, TableActionGroup } from "@shared/types/data-table"
import type { Plan } from "@/features/subscriptions/types/plans/plan.types"
import { type BaseActionHandlers } from "./base-actions"

interface PlanSpecificHandlers {
  onViewPlan?: (plan: Plan) => void
  onUpdatePlan?: (plan: Plan) => void
  onUpdateDraftPlan?: (plan: Plan) => void
  onDuplicatePlan?: (plan: Plan) => void
  onDeactivatePlan?: (plan: Plan) => void
  onBulkDeactivate?: (planIds: string[]) => void
  onBulkDuplicate?: (planIds: string[]) => void
}

export type PlanActionHandlers = BaseActionHandlers<Plan> & PlanSpecificHandlers

// Row actions for tables - grouped to match subscription pattern
const createPlanRowActions = (handlers: PlanActionHandlers): TableActionGroup<Plan>[] => [
  {
    label: "ACTIONS",
    actions: [
      {
        key: "viewPlan",
        label: "View plan",
        onClick: (plan: Plan) => handlers.onViewPlan?.(plan),
      },
      {
        key: "duplicatePlan",
        label: "Duplicate plan",
        onClick: (plan: Plan) => handlers.onDuplicatePlan?.(plan),
      },
      {
        key: "deactivatePlan",
        label: "Archive plan",
        onClick: (plan: Plan) => handlers.onDeactivatePlan?.(plan),
        variant: "destructive" as const,
      },
    ]
  },
]

// Panel actions for detail pages - comprehensive operations
const createPlanPanelActions = (handlers: PlanActionHandlers): TableActionGroup<Plan>[] => [
  {
    label: "ACTIONS",
    actions: [
      {
        key: "updatePlan",
        label: "Update plan",
        onClick: (plan: Plan) => handlers.onUpdatePlan?.(plan),
      },
      {
        key: "updateDraftPlan",
        label: "Update draft plan",
        onClick: (plan: Plan) => handlers.onUpdateDraftPlan?.(plan),
      },
      {
        key: "deactivatePlan",
        label: "Archive plan",
        onClick: (plan: Plan) => handlers.onDeactivatePlan?.(plan),
        variant: "destructive" as const,
      },
    ]
  },
]

export const getPlanActions = (handlers: PlanActionHandlers = {}) => {
  return {
    bulkActions: [
      {
        key: "duplicate",
        label: "Duplicate",
        onClick: (ids: string[]) => handlers.onBulkDuplicate?.(ids),
        disabled: (ids: string[]) => ids.length === 0,
      },
      {
        key: "deactivate",
        label: "Archive",
        onClick: (ids: string[]) => handlers.onBulkDeactivate?.(ids),
        disabled: (ids: string[]) => ids.length === 0,
      },
    ],
    rowActions: createPlanRowActions(handlers),
  }
}

export const getPlanBulkActions = (handlers: PlanActionHandlers = {}) =>
  getPlanActions(handlers).bulkActions

export const getPlanRowActions = (handlers: PlanActionHandlers = {}) =>
  getPlanActions(handlers).rowActions

export const getPlanPanelActions = (handlers: PlanActionHandlers = {}) =>
  createPlanPanelActions(handlers)

export const getPlanHoverActions = (handlers: PlanActionHandlers = {}): TableAction<Plan>[] => [
  {
    key: "viewPlan",
    label: "View plan",
    onClick: (plan: Plan) => handlers.onViewPlan?.(plan),
  },
]
